import {
  ChangeEvent,
  FC,
  useRef,
  useState,
  KeyboardEvent,
  useEffect,
} from "react";
import { Field } from "formik";
// make sure you have react-hot-toast installed

import ErrorMessage from "../ErrorMessage";
import { X, CirclePlus } from "lucide-react";
import {
  InputWrapper,
  StyledInput,
  Tag,
  Wrapper,
  SuggestionsList,
  SuggestionItem,
} from "./style";
import { StyleLabel } from "../style";
import theme from "@utils/theme";
import { toast } from "react-toastify";

interface TagInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  styleWrapper?: string;
  styleLabel?: string;
  data?: string[];
  icon?: boolean;
  allowSame?: boolean;
}

const TagInput: FC<TagInputProps> = ({
  name,
  label,
  placeholder = "Enter value",
  styleWrapper = "",
  styleLabel = "",
  data = [],
  icon = true,
  allowSame = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const hasError = meta.touched && meta.error;

        const addTag = (value?: string, fromTyping = false) => {
          const val = value ?? inputValue.trim();
          if (val === "") return;
          const currentValues = field.value || [];

          if (!allowSame) {
            if (currentValues.includes(val)) {
              if (fromTyping) toast.warning(`${val} is already added`);
              return;
            }
          }

          // ONLY allow adding values if data is passed
          if (data.length > 0) {
            if (data.includes(val)) {
              const newValues = [...currentValues, val];
              form.setFieldValue(name, newValues);
            } else {
              if (fromTyping) toast.warning(`${val} is not in suggestions`);
              return;
            }
          } else {
            const newValues = [...currentValues, val];
            form.setFieldValue(name, newValues);
          }

          setInputValue("");
          setShowSuggestions(false);
          if (data.length > 0 && inputRef.current) {
            inputRef.current.blur();
          }
        };

        const removeTag = (index: number) => {
          const currentValues = field.value || [];
          const newValues = currentValues.filter((_, i) => i !== index);
          form.setFieldValue(name, newValues);
        };

        const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addTag(undefined, true); // true = typed manually
          }
        };

        const filteredSuggestions = data.filter(
          (item) =>
            item.toLowerCase().includes(inputValue.toLowerCase()) &&
            !(field.value || []).includes(item)
        );

        useEffect(() => {
          if (inputValue && filteredSuggestions.length > 0) {
            setShowSuggestions(true);
          }
        }, [inputValue, filteredSuggestions.length]);

        return (
          <Wrapper styleWrapper={styleWrapper}>
            {label && (
              <StyleLabel hasError={!!hasError} className={styleLabel}>
                {label}
              </StyleLabel>
            )}

            <InputWrapper hasError={!!hasError}>
              {Array.isArray(field.value) &&
                field.value.map((tag: string, index: number) => (
                  <Tag key={index}>
                    {tag}
                    <X size={14} onClick={() => removeTag(index)} />
                  </Tag>
                ))}

              <div style={{ position: "relative", width: "100%" }}>
                <StyledInput
                  ref={inputRef}
                  type="text"
                  placeholder={placeholder}
                  value={inputValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setInputValue(e.target.value);
                    if (data.length > 0) setShowSuggestions(true);
                  }}
                  onKeyDown={handleKeyDown}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 150)
                  }
                  onFocus={() => data.length > 0 && setShowSuggestions(true)}
                />

                {showSuggestions && filteredSuggestions.length > 0 && (
                  <SuggestionsList onMouseDown={(e) => e.stopPropagation()}>
                    {filteredSuggestions.map((item, idx) => (
                      <SuggestionItem
                        key={idx}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addTag(item); // from suggestion, no toast
                        }}
                      >
                        {item}
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                )}
              </div>

              {icon && (
                <CirclePlus
                  style={{
                    position: "absolute",
                    right: "25.5%",
                    width: "20px",
                    cursor: "pointer",
                    color: theme.colors.primary,
                  }}
                  onClick={() => addTag(undefined, true)} // clicked manually
                />
              )}
            </InputWrapper>

            {hasError && <ErrorMessage message={meta.error} />}
          </Wrapper>
        );
      }}
    </Field>
  );
};

export default TagInput;
